import { Injectable } from '@angular/core';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { environment } from '../../environments/environment';
import { AngularFireAuth } from '@angular/fire/compat/auth';

interface CachedCredentials {
  credentials: any;
  expiration: Date;
  region: string;
}

@Injectable({
  providedIn: 'root'
})
export class CognitoCredentialsService {
  private credentialsCache: Map<string, CachedCredentials> = new Map();

  constructor(private afAuth: AngularFireAuth) {}

  /**
   * Extract region from Identity Pool ID (format: region:uuid)
   */
  private getIdentityPoolRegion(): string {
    const identityPoolId = environment.cognito.identityPoolId;
    // Identity Pool ID format: us-east-1:xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
    const parts = identityPoolId.split(':');
    if (parts.length >= 2) {
      return parts[0]; // Return the region part
    }
    // Fallback to configured region if ID format is unexpected
    return environment.cognito.region;
  }

  /**
   * Get temporary AWS credentials from Cognito Identity Pool using Firebase token
   * @param region AWS region for S3 operations (can be different from Identity Pool region)
   * @returns Promise resolving to AWS credentials
   */
  async getCredentials(region: string = environment.cognito.region): Promise<any> {
    const cacheKey = `${region}`;
    
    // Check cache first
    const cached = this.credentialsCache.get(cacheKey);
    if (cached && this.isCredentialsValid(cached)) {
      return cached.credentials;
    }

    try {
      // Get current Firebase user and ID token
      const user = await this.afAuth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated with Firebase');
      }

      // Get Firebase ID token
      const idToken = await user.getIdToken();
      
      if (!idToken) {
        throw new Error('Failed to retrieve Firebase ID token');
      }

      // Extract region from Identity Pool ID (Identity Pools are region-specific)
      const identityPoolRegion = this.getIdentityPoolRegion();

      // Create Cognito Identity client with the Identity Pool's region
      const cognitoClient = new CognitoIdentityClient({
        region: identityPoolRegion
      });

      // Get credentials from Cognito Identity Pool using Firebase token
      const credentialsProvider = fromCognitoIdentityPool({
        client: cognitoClient,
        identityPoolId: environment.cognito.identityPoolId,
        logins: {
          // Map Firebase OIDC provider to the ID token
          [environment.cognito.oidcProvider]: idToken
        }
      });

      const credentials = await credentialsProvider();
      
      // Cache credentials with expiration
      const expiration = credentials.expiration 
        ? new Date(credentials.expiration) 
        : new Date(Date.now() + 3600000); // Default 1 hour

      this.credentialsCache.set(cacheKey, {
        credentials,
        expiration,
        region
      });

      return credentials;
    } catch (error: any) {
      console.error('Error getting Cognito credentials:', error);
      
      // Provide more specific error messages
      if (error.name === 'NotAuthorizedException') {
        throw new Error('Firebase token is invalid or expired. Please log in again.');
      } else if (error.name === 'ResourceNotFoundException') {
        throw new Error('Cognito Identity Pool not found. Check your configuration.');
      }
      
      throw error;
    }
  }

  /**
   * Check if cached credentials are still valid
   */
  private isCredentialsValid(cached: CachedCredentials): boolean {
    if (!cached || !cached.expiration) {
      return false;
    }
    // Consider credentials valid if they expire in more than 5 minutes
    return cached.expiration.getTime() > Date.now() + 300000;
  }

  /**
   * Clear credentials cache (useful for logout or token refresh)
   */
  clearCache(): void {
    this.credentialsCache.clear();
  }

  /**
   * Clear cache for a specific region
   */
  clearCacheForRegion(region: string): void {
    this.credentialsCache.delete(region);
  }

  /**
   * Force refresh credentials (clears cache and fetches new ones)
   */
  async refreshCredentials(region: string = environment.cognito.region): Promise<any> {
    this.clearCacheForRegion(region);
    return this.getCredentials(region);
  }
}

